import React, { Fragment, useState, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useSelector, useDispatch } from "react-redux";
import { updatenavPopup, setShowPopupFalse } from "../../redux/navPopupSlice";
import {
  ChevronDownIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import {
  HomeIcon,
  TruckIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  BookOpenIcon,
  ReceiptPercentIcon,
  SunIcon,
  UsersIcon,
  SquaresPlusIcon,
  BuildingOfficeIcon,
  AdjustmentsHorizontalIcon,
  UserCircleIcon,
  MapIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import { capitalizeFirstLetter, classNames, getNavigationDisplayName } from "../../utils/helperFuctions";
import { initNav, settingNav } from "../../redux/navigationSlice";
import { getCookie } from "../../utils/cookieHandler";
import PopUp from "../PopUp/PopUp";
import { resetpageHeader, updatepageHeader } from "../../redux/pageHeaderSlice";
import { config } from "../../utils/config";
import axios from "axios";

const SideBar = ({
  isOpen,
  setSidebarOpen,
  navigation,
  clickFunction,
  settingsOpened,
  settingsRoles,
  collapsed, // controlled by parent
  onToggleCollapse, // controlled by parent
  signOutClick,
}) => {
  const [popupData, setpopupData] = useState({});
  const navPopup = useSelector((state) => state.navPopup);
  const dispatch = useDispatch();
  const pathname = useLocation().pathname;
  const navigate = useNavigate();

  // fetch version
  const [version, setVersion] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${config.apiUrl}/api/changelogs/latest`, {
          headers: { Authorization: `Bearer ${getCookie("_AT")}` },
        });
        setVersion(res.data);
      } catch (e) {
        console.error("Error fetching version:", e);
      }
    })();
  }, []);

  // active link
  const getNameByHref = (href) => {
    let activeLink =
      navigation.find((item) => item.href === href)?.name || null;
    if (activeLink == null) {
      navigation.forEach((nav) => {
        if (nav.subItems != null) {
          const hit = nav.subItems.find((sub) => sub.href === href);
          if (hit) activeLink = hit.name;
        }
      });
    }
    return activeLink;
  };

  const [activeLink, setActiveLink] = useState(getNameByHref(pathname));
  useEffect(() => {
    const foundName = getNameByHref(pathname);
    if (foundName) {
      setActiveLink(foundName);
    } else {
      // Use the navigation display name mapping for proper titles
      // Pass both main and settings navigation arrays to ensure we find the correct name
      const allNavigationArrays = [initNav, settingNav];
      setActiveLink(getNavigationDisplayName(pathname, navigation, allNavigationArrays));
    }
  }, [pathname, navigation]);

  const currentRole = getCookie("_r");

  // icon map
  const iconMap = {
    HomeIcon,
    TruckIcon,
    MapPinIcon,
    CurrencyDollarIcon,
    BookOpenIcon,
    ReceiptPercentIcon,
    SunIcon,
    UsersIcon,
    SquaresPlusIcon,
    BuildingOfficeIcon,
    AdjustmentsHorizontalIcon,
    UserCircleIcon,
    MapIcon,
    BanknotesIcon,
    ChartBarIcon,
  };

  const mapNavIcons = (iconMap, navigation) =>
    navigation.map((item) => {
      const updated = { ...item, icon: iconMap[item.icon] || item.icon };
      if (Array.isArray(item.subItems)) {
        updated.subItems = mapNavIcons(iconMap, item.subItems);
      }
      return updated;
    });

  const _navigation = mapNavIcons(iconMap, navigation);

  // navigation handler
  const onLinkClicked = (href, itemName, hasSub) => {
    if (activeLink === itemName) {
      dispatch(updatenavPopup({ backClicked: false }));
    }
    if (navPopup.showPopup === true && hasSub !== true) {
      setpopupData({
        isError: true,
        title: "Are you sure you want to exit?",
        description: "You might lose unsaved changes.",
        showPopUp: true,
        takeActionText: "Yes",
        takeActionFun: () => {
          setActiveLink(itemName);
          dispatch(setShowPopupFalse());
          if (
            window.location.pathname.split("/").filter((x) => x !== "").length >
            1
          ) {
            dispatch(
              updatepageHeader({
                secondaryButton: false,
                secondaryButtonText: null,
                secondaryButtonFunction: null,
              })
            );
            dispatch(
              updatepageHeader({
                addButton: false,
                buttonText: null,
                buttonFunction: null,
              })
            );
          }
          navigate(href);
        },
      });
    } else {
      if (
        window.location.pathname.split("/").filter((x) => x !== "").length > 1
      ) {
        dispatch(resetpageHeader());
      }
      if (hasSub !== true) {
        setActiveLink(itemName);
        setSidebarOpen(false);
      }
      navigate(href);
    }
  };

  // ===== Collapsed fly-out state (CLEAN MENU) =====
  const sidebarRef = useRef(null);
  const flyoutRef = useRef(null);
  const [flyout, setFlyout] = useState({
    open: false,
    top: 0,
    left: 0,
    items: [],
    parent: "",
  });

  const openFlyout = (parentItem, triggerEl) => {
    if (!collapsed) return;
    const triggerRect = triggerEl.getBoundingClientRect();
    const sidebarRect = sidebarRef.current?.getBoundingClientRect();
    const left = (sidebarRect?.right || triggerRect.right) + 8;

    // clamp top within viewport with a small margin
    const margin = 8;
    const top = Math.max(
      margin,
      Math.min(triggerRect.top, window.innerHeight - margin - 300)
    ); // 300px is a reasonable default; panel is scrollable anyway

    setFlyout({
      open: true,
      top,
      left,
      items: parentItem.subItems || [],
      parent: parentItem.name,
    });
  };

  const closeFlyout = () => setFlyout((f) => ({ ...f, open: false }));

  // close on outside/escape/scroll/resize
  useEffect(() => {
    if (!flyout.open) return;
    const handleDown = (e) => {
      if (flyoutRef.current && flyoutRef.current.contains(e.target)) return;
      closeFlyout();
    };
    const handleEsc = (e) => e.key === "Escape" && closeFlyout();
    window.addEventListener("mousedown", handleDown, true);
    window.addEventListener("keydown", handleEsc);
    window.addEventListener("scroll", closeFlyout, true);
    window.addEventListener("resize", closeFlyout);
    return () => {
      window.removeEventListener("mousedown", handleDown, true);
      window.removeEventListener("keydown", handleEsc);
      window.removeEventListener("scroll", closeFlyout, true);
      window.removeEventListener("resize", closeFlyout);
    };
  }, [flyout.open]);

  return (
    <>
      {/* ===== Mobile sidebar ===== */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-secondary/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5 w-full"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </Transition.Child>

                <div
                  className={`flex grow flex-col overflow-y-auto px-6 ${
                    settingsOpened ? "bg-gray-500" : "bg-primary"
                  }`}
                >
                  <div className="flex h-16 shrink-0 items-center pb-4 mb-5">
                    <img
                      className="h-6 w-auto"
                      src="/logo-dark.png"
                      alt="Car2Go"
                    />
                  </div>

                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2 space-y-1">
                          {mapNavIcons(iconMap, navigation).map((item) => (
                            <React.Fragment key={item.name}>
                              {Array.isArray(item.allowedroles) &&
                                item.allowedroles.includes(currentRole) && (
                                  <li
                                    key={item.name}
                                    style={{
                                      backgroundColor:
                                        activeLink === item.name ||
                                        (item.subItems != null &&
                                          item.subItems.find(
                                            (x) => x.name === activeLink
                                          ) != null)
                                          ? "#374151"
                                          : "unset",
                                    }}
                                    className={classNames(
                                      "rounded-md",
                                      item.open ? "bg-gray-700" : ""
                                    )}
                                    onClick={clickFunction}
                                  >
                                    <button
                                      style={{ cursor: "pointer" }}
                                      name={item.name}
                                      className={classNames(
                                        "text-white hover:text-white hover:bg-gray-700",
                                        "group flex gap-x-3 w-full rounded-md p-2 text-sm leading-6 font-semibold items-center relative"
                                      )}
                                      onClick={() =>
                                        onLinkClicked(
                                          item.href,
                                          item.name,
                                          item.subItems != null
                                        )
                                      }
                                    >
                                      <item.icon className="h-6 w-6 shrink-0" />
                                      {item.name}
                                      {item.subItems && (
                                        <ChevronDownIcon
                                          className={classNames(
                                            item.current || item.open
                                              ? "rotate-[-90deg]"
                                              : "",
                                            "h-4 w-4 shrink-0 absolute right-3"
                                          )}
                                        />
                                      )}
                                    </button>

                                    {item.subItems && (
                                      <ul
                                        role="list"
                                        className={`ml-2.5 my-2.5 sub-item ${
                                          item.current || item.open
                                            ? ""
                                            : "hidden"
                                        }`}
                                      >
                                        {item.subItems.map((subItem) => {
                                          if (
                                            (Array.isArray(
                                              subItem.allowedroles
                                            ) &&
                                              subItem.allowedroles.includes(
                                                currentRole
                                              )) === false
                                          ) {
                                            return null;
                                          }
                                          return (
                                            <li
                                              key={subItem.name}
                                              onClick={clickFunction}
                                            >
                                              <button
                                                name={subItem.name}
                                                style={{ cursor: "pointer" }}
                                                className={classNames(
                                                  "text-blue-200 hover:text-white hover:bg-gray-700",
                                                  "group flex gap-x-3 w-full rounded-md p-2 text-sm leading-6 font-semibold"
                                                )}
                                                onClick={() =>
                                                  onLinkClicked(
                                                    subItem.href,
                                                    subItem.name
                                                  )
                                                }
                                              >
                                                {subItem.name}
                                              </button>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    )}
                                  </li>
                                )}
                            </React.Fragment>
                          ))}
                        </ul>
                      </li>

                      <li className="mt-auto space-y-1">
                        {Array.isArray(settingsRoles) &&
                          settingsRoles.includes(currentRole) && (
                            <div onClick={clickFunction}>
                              <button
                                type="button"
                                name="settingsButton"
                                className="group -mx-2 flex w-full gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-blue-200 hover:bg-gray-700 hover:text-white"
                                onClick={clickFunction}
                              >
                                <Cog6ToothIcon className="h-6 w-6 shrink-0 text-blue-200 group-hover:text-white" />
                                Settings
                                {settingsOpened && (
                                  <span className="ml-auto text-xs opacity-70">
                                    Open
                                  </span>
                                )}
                              </button>
                            </div>
                          )}
                        {signOutClick && (
                          <div>
                            <button
                              type="button"
                              onClick={signOutClick}
                              className="group -mx-2 flex w-full items-center gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-blue-200 hover:bg-gray-700 hover:text-white"
                            >
                              <ArrowRightOnRectangleIcon className="h-6 w-6 shrink-0 text-blue-200 group-hover:text-white" />
                              <span className="truncate">Sign out</span>
                              {getCookie("_n") && (
                                <span className="ml-auto text-xs text-blue-200 group-hover:text-white truncate max-w-[110px]">
                                  {getCookie("_n")}
                                </span>
                              )}
                            </button>
                          </div>
                        )}
                      </li>
                    </ul>
                  </nav>

                  {version != null && (
                    <div
                      onClick={() => {
                        navigate("/changelogs");
                        setSidebarOpen(false);
                      }}
                      className="cursor-pointer mb-2 hover:underline"
                      style={{
                        color: "#ffffff50",
                        textAlign: "center",
                        fontSize: 12,
                      }}
                    >
                      Version {version}
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* ===== Desktop sidebar ===== */}
      <div
        ref={sidebarRef}
        className={classNames(
          "hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-200 ease-in-out",
          collapsed ? "lg:w-20" : "lg:w-72"
        )}
      >
        <div
          className={classNames(
            "flex grow flex-col overflow-y-auto h-full",
            collapsed ? "px-3" : "px-6",
            settingsOpened ? "bg-gray-500" : "bg-primary"
          )}
        >
          {/* header */}
          <div
            className={classNames(
              "flex items-center mb-5",
              collapsed ? "h-14 justify-center" : "h-16 justify-between"
            )}
          >
            {!collapsed && (
              <img className="h-6 w-auto" src="/logo-dark.png" alt="Car2Go" />
            )}
            <button
              type="button"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              onClick={() => {
                closeFlyout();
                onToggleCollapse();
              }}
              className="rounded-md p-2 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
            >
              {collapsed ? (
                <ChevronDoubleRightIcon className="h-5 w-5" />
              ) : (
                <ChevronDoubleLeftIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {_navigation.map((item) => (
                    <React.Fragment key={item.name}>
                      {Array.isArray(item.allowedroles) &&
                        item.allowedroles.includes(currentRole) && (
                          <li
                            key={item.name}
                            className={classNames(
                              "rounded-md relative",
                              item.open ? "bg-gray-700" : ""
                            )}
                            style={{
                              backgroundColor:
                                activeLink === item.name ||
                                (item.subItems != null &&
                                  item.subItems.find(
                                    (x) => x.name === activeLink
                                  ) != null)
                                  ? "#374151"
                                  : "unset",
                            }}
                            onClick={clickFunction}
                          >
                            <button
                              name={item.name}
                              // NOTE: no title — avoids native black tooltips over the flyout
                              className={classNames(
                                "text-white hover:text-white hover:bg-gray-700",
                                "group flex w-full rounded-md p-2 text-sm leading-6 font-semibold items-center relative",
                                collapsed ? "justify-center gap-x-0" : "gap-x-3"
                              )}
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                if (collapsed && item.subItems?.length) {
                                  e.preventDefault();
                                  e.stopPropagation(); // don't bubble to li's clickFunction
                                  openFlyout(item, e.currentTarget);
                                  return;
                                }
                                onLinkClicked(
                                  item.href,
                                  item.name,
                                  item.subItems != null
                                );
                              }}
                            >
                              <item.icon className="h-6 w-6 shrink-0" />
                              <span
                                className={classNames(
                                  collapsed ? "sr-only" : ""
                                )}
                              >
                                {item.name}
                              </span>
                              {!collapsed && item.subItems && (
                                <ChevronDownIcon
                                  className={classNames(
                                    item.current || item.open
                                      ? "rotate-[-90deg]"
                                      : "",
                                    "h-4 w-4 shrink-0 absolute right-3"
                                  )}
                                />
                              )}
                            </button>

                            {/* Expanded (not collapsed) nested list */}
                            {!collapsed && item.subItems && (
                              <ul
                                role="list"
                                className={classNames(
                                  "ml-2.5 my-2.5 sub-item",
                                  item.current || item.open ? "" : "hidden"
                                )}
                              >
                                {item.subItems.map((subItem) => {
                                  if (
                                    (Array.isArray(subItem.allowedroles) &&
                                      subItem.allowedroles.includes(
                                        currentRole
                                      )) === false
                                  )
                                    return null;

                                  return (
                                    <li
                                      key={subItem.name}
                                      onClick={clickFunction}
                                    >
                                      <button
                                        name={subItem.name}
                                        className={classNames(
                                          subItem.name === activeLink
                                            ? "text-white"
                                            : "text-blue-200",
                                          "hover:text-white hover:bg-gray-700",
                                          "group flex w-full gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold"
                                        )}
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                          onLinkClicked(
                                            subItem.href,
                                            subItem.name
                                          )
                                        }
                                      >
                                        {subItem.name}
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        )}
                    </React.Fragment>
                  ))}
                </ul>
              </li>

              <li className="mt-auto space-y-1">
                {Array.isArray(settingsRoles) &&
                  settingsRoles.includes(currentRole) && (
                    <div
                      className={collapsed ? "flex justify-center" : ""}
                      onClick={clickFunction}
                    >
                      <button
                        type="button"
                        name="settingsButton"
                        className={classNames(
                          "group -mx-2 flex rounded-md w-full p-2 text-sm font-semibold leading-6 text-blue-200 hover:bg-gray-700 hover:text-white",
                          collapsed ? "justify-center" : "gap-x-3"
                        )}
                        onClick={clickFunction}
                      >
                        <Cog6ToothIcon className="h-6 w-6 shrink-0 text-blue-200 group-hover:text-white" />
                        {!collapsed && "Settings"}
                        {settingsOpened && !collapsed && (
                          <XMarkIcon
                            name="settingsButton"
                            className="h-6 w-6 text-blue-200 group-hover:text-white ml-auto"
                          />
                        )}
                      </button>
                    </div>
                  )}
                {signOutClick && (
                  <div className={collapsed ? "flex justify-center" : ""}>
                    <button
                      type="button"
                      onClick={signOutClick}
                      className={classNames(
                        "group -mx-2 flex items-center rounded-md w-full p-2 text-sm font-semibold leading-6 text-blue-200 hover:bg-gray-700 hover:text-white",
                        collapsed ? "justify-center" : "gap-x-3"
                      )}
                    >
                      <ArrowRightOnRectangleIcon className="h-6 w-6 shrink-0 text-blue-200 group-hover:text-white" />
                      {!collapsed && (
                        <>
                          <span className="truncate">Sign out</span>
                          {getCookie("_n") && (
                            <span className="ml-auto text-xs text-blue-200 group-hover:text-white truncate max-w-[110px]">
                              {getCookie("_n")}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </li>
            </ul>
          </nav>

          {!collapsed && version != null && (
            <div
              onClick={() => {
                navigate("/changelogs");
                setSidebarOpen(false);
              }}
              className="cursor-pointer hover:underline mb-2"
              style={{ color: "#ffffff50", textAlign: "center", fontSize: 12 }}
            >
              Version {version}
            </div>
          )}
        </div>
      </div>

      {/* ===== Collapsed fly-out panel (fixed & clean) ===== */}
      {collapsed && flyout.open && (
        <div
          ref={flyoutRef}
          className="fixed z-[60] max-h-[calc(100vh-16px)] overflow-y-auto rounded-lg bg-gray-800 text-white shadow-2xl ring-1 ring-black/20"
          style={{ top: flyout.top, left: flyout.left, minWidth: 224 }} // 56 * 4 = 224px
        >
          {flyout.parent && (
            <div className="px-3 pt-3 pb-2 text-xs uppercase tracking-wide text-gray-300/80">
              {flyout.parent}
            </div>
          )}
          <div className="py-2">
            {flyout.items.map((subItem) => {
              if (
                (Array.isArray(subItem.allowedroles) &&
                  subItem.allowedroles.includes(currentRole)) === false
              )
                return null;

              return (
                <button
                  key={subItem.name}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700/80 focus:bg-gray-700/80 outline-none"
                  onClick={(e) => {
                    e.preventDefault();
                    closeFlyout();
                    onLinkClicked(subItem.href, subItem.name);
                  }}
                >
                  {subItem.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* PopUp */}
      <PopUp
        autoClose={popupData.autoClose}
        isError={popupData.isError}
        title={popupData.title}
        description={popupData.description}
        formOptions={popupData.formOptions}
        takeActionText={popupData.takeActionText}
        takeActionFun={popupData.takeActionFun}
        showPopUp={popupData.showPopUp}
        hidePopUp={() => setpopupData({})}
        formSubmit={popupData.formSubmit}
        formConcatData={popupData.formConcatData}
        propName={popupData.propName}
        disabledForm={popupData.disabledForm}
        hideCancel={popupData.hideCancel}
        hideMainButton={popupData.hideMainButton}
        showLoading={popupData.hideMainButton}
        progressValue={popupData.progressValue}
        overflow={popupData.overflow}
        expand={popupData.expand}
      >
        {popupData.children}
      </PopUp>
    </>
  );
};

export default SideBar;
